import { PluginListenerHandle } from "@capacitor/core";
import { PushNotificationSchema } from "@capacitor/push-notifications";
import React, { createContext, useEffect, useState } from "react";

import { Analytics, AnalyticsEvents } from "modules/analytics";
import {
  getAppVersion,
  isAppVersionOutdated,
  MIN_REQUIRED_APP_VERSION,
} from "modules/appVersion";
import { useUser } from "modules/user";
import { getCurrentPlatform } from "utils/platform.helpers";

import { PushNotificationActionPerformed } from "../IPushNotificationAdapter";
import { PushNotificationService } from "../PushNotificationService/PushNotificationService";
import { useDeepLinkNavigation } from "../useDeepLinkNavigation/useDeepLinkNavigation";

type NotificationProviderProps = {
  readonly children: React.ReactNode;
  readonly isNativePlatform: boolean;
  readonly pushNotificationService: PushNotificationService;
};

type PushNotificationContext = {
  readonly pushNotifications: PushNotificationSchema[];
  readonly actionsPerformed: PushNotificationActionPerformed<unknown>[];
  readonly deviceToken?: string;
};

export default function PushNotificationProvider({
  children,
  isNativePlatform,
  pushNotificationService,
}: NotificationProviderProps): JSX.Element {
  const { handleNotificationAction } = useDeepLinkNavigation();

  const [pushNotifications, setPushNotifications] = useState<
    PushNotificationSchema[]
  >([]);
  const [actionsPerformed, setActionsPerformed] = useState<
    PushNotificationActionPerformed<unknown>[]
  >([]);
  const [deviceToken, setDeviceToken] = useState<string>();

  const { user } = useUser();

  const initializePushNotifications = async () => {
    if (!isNativePlatform) {
      console.log(
        "[RMD]: not on native platform, not initializing push notifications."
      );
      return false;
    }

    const appVersion = await getAppVersion();

    if (appVersion && isAppVersionOutdated(appVersion)) {
      console.log(
        "[RMD]: does not meet minimum required app version, not initializing push notifications.",
        appVersion,
        MIN_REQUIRED_APP_VERSION
      );
      return false;
    }

    if (!user?.id) {
      console.log(
        "[RMD]: user not found, not initializing push notifications."
      );
      return false;
    }

    try {
      await pushNotificationService.register();
      console.log(
        "[RMD]: Registered for push notifications (will retrieve token if permissions already granted)"
      );
    } catch (error) {
      console.log(
        "[RMD]: Initial registration attempt failed (likely no permissions yet)",
        error
      );
    }

    // Then check if we need to request permissions
    const permissionsGranted =
      await pushNotificationService.requestPermission();

    if (permissionsGranted) {
      await handlePermissionGranted();
    } else {
      await handlePermissionDenied();
    }
  };

  const handlePermissionGranted = async (): Promise<void> => {
    await pushNotificationService.register();
  };

  const handlePermissionDenied = async (): Promise<void> => {
    trackNotificationPermissionDenied();
  };

  const registerListeners = async (): Promise<PluginListenerHandle[]> => {
    const onNotificationListener =
      await pushNotificationService.onNotificationReceived((notification) => {
        console.log("[RMD]: onNotificationReceived", notification);
        setPushNotifications((prev) => [...prev, notification]);
      });

    const onTokenListener = await pushNotificationService.onTokenReceived(
      (token) => {
        setDeviceToken(token.value);
      }
    );

    const onActionListener =
      await pushNotificationService.onNotificationActionPerformed((action) => {
        console.log("[RMD]: onNotificationActionPerformed", action);
        setActionsPerformed((prev) => [...prev, action]);

        handleNotificationAction(action);
      });

    return [onNotificationListener, onTokenListener, onActionListener];
  };

  const trackUserDeviceToken = (deviceToken: string, userId: string): void => {
    Analytics.track(
      AnalyticsEvents.DEVICE_TOKEN_REGISTERED,
      {},
      {
        context: {
          userId,
          device: {
            token: deviceToken,
            type: getCurrentPlatform(),
          },
        },
      }
    );
  };

  const trackNotificationPermissionDenied = (): void => {
    Analytics.track(AnalyticsEvents.PUSH_NOTIFICATION_PERMISSIONS_DENIED, {});
  };

  useEffect(() => {
    // Set up listeners early so we can capture device tokens on app open
    // even before explicit permission is granted (for users who already granted permission)
    if (!isNativePlatform || !user?.id) {
      return () => undefined;
    }

    let canceled = false;
    let activeListeners: PluginListenerHandle[] = [];

    registerListeners().then((listeners) => {
      if (canceled) {
        for (const l of listeners) {
          l.remove();
        }
        return;
      }
      activeListeners = listeners;
    });

    return () => {
      canceled = true;
      for (const listener of activeListeners) {
        listener.remove();
      }
    };
  }, [isNativePlatform, user?.id]);

  useEffect(() => {
    if (deviceToken && user?.id) {
      trackUserDeviceToken(deviceToken, user?.id);
    }
  }, [deviceToken, user?.id]);

  useEffect(() => {
    initializePushNotifications();
  }, [user?.id]);

  const value = {
    pushNotifications,
    deviceToken,
    actionsPerformed,
  };

  return (
    <PushNotificationContext.Provider value={value}>
      {children}
    </PushNotificationContext.Provider>
  );
}

export const PushNotificationContext = createContext<PushNotificationContext>({
  pushNotifications: [],
  actionsPerformed: [],
  deviceToken: undefined,
});
